import React, { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import dynamic from 'next/dynamic';

import { scaleLinear } from 'd3-scale';
import { followCursor } from 'tippy.js/headless';

import useFormatNumber, { getNumberFormat } from '@/hooks/format/useFormatNumber';

//Libs
import { worldMap } from '@/utils/chartmaps/world-110m';
//Hooks
import useDarkMode from '@/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

import { darkModeColorsChart } from '@/lib/analytics/colors';

import { SizeMe } from '@/components/base';
import Tooltip from '@/components/base/Tooltip';
//Components
import ChartWrapper from '@/components/composite/Charts/ChartWrapper';
import { TooltipMapChart } from '@/components/composite/Charts/components/TooltipMapChart';

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const ChoroplethMapChartComponent = ({
    data,
    columnNames,
    activeTable,
    userNumbersFormat,
    isExport,
    height = 260
}) => {
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const [tooltipContent, setTooltipContent] = useState(null);

    const myArray = data.graphData;
    let lowest = 0;
    let highest = 0;
    let tmp;
    for (let i = myArray.length - 1; i >= 0; i--) {
        tmp = myArray[i].value;
        if (tmp < lowest) lowest = tmp;
        if (tmp > highest) highest = tmp;
    }

    const colorScale = scaleLinear()
        .domain([lowest, highest])
        .range([darkModeColorsChart[theme]['bgminmap'], darkModeColorsChart[theme]['bgmaxmap']]);

    const finalData = useMemo(() => {
        let arrayObj = [];

        data.graphData.forEach(function (v) {
            let obj = { ...v };
            delete obj.country;
            arrayObj.push(obj);
        });

        return arrayObj;
    }, [data.graphData]);

    const finalColumnNames = useMemo(() => {
        let obj = columnNames;

        delete obj.country;

        return obj;
    }, [columnNames]);

    return (
        <>
            {activeTable || isExport ? (
                <DynamicTableChart
                    graphData={finalData}
                    isExport={isExport}
                    columnNames={finalColumnNames}
                    userNumbersFormat={userNumbersFormat}
                    defaultTableSorting={data?.defaultTableSorting ?? null}
                />
            ) : (
                <>
                    <SizeMe>
                        {({ size }) => (
                            <Tooltip
                                placement="top"
                                arrow={false}
                                content={
                                    tooltipContent ? (
                                        <TooltipMapChart
                                            name={tooltipContent.country}
                                            value={tooltipContent.value}
                                            percentage={tooltipContent.percentage}
                                        />
                                    ) : null
                                }
                                followCursor
                                plugins={[followCursor]}>
                                <ComposableMap
                                    projectionConfig={{
                                        scale: 100
                                    }}
                                    width={isExport ? 740 : size?.width ?? 0 + 100}
                                    height={isExport ? 300 : height}
                                    style={{
                                        width: '100%',
                                        height: 'auto'
                                    }}>
                                    <ZoomableGroup zoom={1}>
                                        {data.graphData.length > 0 && (
                                            <Geographies geography={worldMap}>
                                                {({ geographies }) =>
                                                    geographies.map((geo) => {
                                                        const d = data.graphData.find(
                                                            (s) =>
                                                                s.country === geo.properties.ISO_A2
                                                        );
                                                        return (
                                                            <Geography
                                                                key={geo.rsmKey}
                                                                geography={geo}
                                                                data-testid={
                                                                    d?.value
                                                                        ? 'marker-country'
                                                                        : null
                                                                }
                                                                onMouseEnter={() => {
                                                                    const { ISO_A2, NAME } =
                                                                        geo.properties;
                                                                    const d = data.graphData.find(
                                                                        (s) => s.country === ISO_A2
                                                                    );
                                                                    const value = formatNumber(
                                                                        d ? d.value : 0
                                                                    );
                                                                    const percentage = formatNumber(
                                                                        d ? d.percentage : 0,
                                                                        getNumberFormat('percent')
                                                                    );
                                                                    setTooltipContent({
                                                                        country: d?.name ?? NAME,
                                                                        value,
                                                                        percentage
                                                                    });
                                                                }}
                                                                onMouseLeave={() => {
                                                                    setTooltipContent(null);
                                                                }}
                                                                fill={
                                                                    d?.value
                                                                        ? colorScale(d.value)
                                                                        : darkModeColorsChart[
                                                                              theme
                                                                          ]['bgminmap']
                                                                }
                                                                stroke={
                                                                    darkModeColorsChart[theme][
                                                                        'strokemap'
                                                                    ]
                                                                }
                                                                style={{
                                                                    default: {
                                                                        outline: 'none'
                                                                    },
                                                                    hover: {
                                                                        outline: 'none'
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })
                                                }
                                            </Geographies>
                                        )}
                                    </ZoomableGroup>
                                </ComposableMap>
                            </Tooltip>
                        )}
                    </SizeMe>
                </>
            )}
        </>
    );
};

ChoroplethMapChartComponent.propTypes = {
    data: PropTypes.object,
    columnNames: PropTypes.object,
    activeTable: PropTypes.bool,
    userNumbersFormat: PropTypes.object
};

const ChoroplethMapChart = ({
    data,
    className = '',
    onReset = () => {
        /** */
    },
    isLoading = false,
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        {...restProps}>
        <ChoroplethMapChartComponent />
    </ChartWrapper>
);

ChoroplethMapChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { ChoroplethMapChart };
